import type { FC } from 'react';

import { getStaticPrefix } from '@eneco/common/env';
import { Badge, Box, Card, IconLoader, Image, Stack, Text } from '@eneco/sparky';
import type { BadgeProps } from '@eneco/sparky/types';

import { buttonContainer, inputContainer } from './ChatEntryPoint.css';

export interface ChatEntryPointProps {
  onClick: () => void;
  badgeColor?: BadgeProps['color'];
  badgeIcon?: string;
  badgeText?: string;
  messageText?: string;
}

/**
 * The chat entry-point card: a clickable card with a badge and a message preview, opening the chat
 * widget on click.
 *
 * Presentational only — it knows nothing about any CMS or the chat widget itself. Lives here rather
 * than beside its caller because `dxp-rules/no-custom-styling` reserves custom vanilla-extract for
 * this library (see the rule's own message in `config/eslint/plugins/dxp-rules/no-custom-styling.mjs`).
 * Extracted from `libs/sitecore/components/content/src/ChatEntryPoint/ChatEntryPoint.tsx`, whose
 * stylesheet moved here verbatim, so the legacy Sitecore lane and `@eneco/cms-components`'
 * CMS-agnostic `ChatEntryPoint` render identically.
 */
export const ChatEntryPoint: FC<ChatEntryPointProps> = ({ onClick, badgeColor, badgeIcon, badgeText, messageText }) => (
  <button className={buttonContainer} onClick={onClick} type="button">
    <Card corners="rounded" elevation="S" overflow="hidden">
      <Box padding="4">
        <Stack direction="row" gap="2" alignY="center">
          <Image borderRadius="rounded" src={`${getStaticPrefix()}/images/eneco/eva-avatar.png`} alt="AI Eva avatar" width="64px" />
          <Stack.Item grow>
            <Stack direction="column" gap="2">
              <Stack.Item>
                <Badge size="S" color={badgeColor}>
                  <Stack gap="1" direction="row">
                    {badgeIcon && (
                      <Stack.Item>
                        <IconLoader size="small" color="currentColor" iconName={badgeIcon} />
                      </Stack.Item>
                    )}
                    <Stack.Item>
                      <Text weight="bold">{badgeText}</Text>
                    </Stack.Item>
                  </Stack>
                </Badge>
              </Stack.Item>
              <Stack.Item>
                <div className={inputContainer}>
                  <Stack direction="row" gap="2" alignY="center">
                    <Stack.Item grow>
                      <Text weight="bold" size="BodyXS">
                        {messageText}
                      </Text>
                    </Stack.Item>
                    <Stack.Item>
                      <IconLoader size="medium" color="graphsElectricitySecondary" iconName="SendIcon" />
                    </Stack.Item>
                  </Stack>
                </div>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        </Stack>
      </Box>
    </Card>
  </button>
);

export default ChatEntryPoint;
